// WinTracker — compact XP + qualifier progress card shown on the Win
// pane. After each banked skill it shows: total XP earned, the live
// pot, and how many wins remain to hit the next qualifier milestone.
import { useEffect, useState } from "react";
import { Trophy, Sparkles, Target } from "lucide-react";
import { AI_MASTERY_PATH } from "@/data/aiMasteryPath";
import {
  BEGINNER_FIRST_AGENT_PEEK_AT,
  BEGINNER_AGENT_UNLOCK_AT,
} from "@/hooks/useBeginnerMode";
import { BUILDER_UNLOCK_AT } from "./BeginnerInlineRunner";

type Milestone = { at: number; label: string };

const MILESTONES: Milestone[] = [
  { at: BUILDER_UNLOCK_AT, label: "Builder mode" },
  { at: BEGINNER_FIRST_AGENT_PEEK_AT, label: "First agent peek" },
  { at: BEGINNER_AGENT_UNLOCK_AT, label: "Agents unlocked" },
  { at: 50, label: "Hackathon qualifier" },
  { at: AI_MASTERY_PATH.length, label: "Full path mastered" },
];

interface Props {
  totalDone: number;
  xpAmount: number;
  potLabel: string;
  bumpKey: number;
}

const WinTracker = ({ totalDone, xpAmount, potLabel, bumpKey }: Props) => {
  const totalXp = totalDone * xpAmount;
  const next = MILESTONES.find((m) => totalDone < m.at);
  const remaining = next ? next.at - totalDone : 0;
  const prevAt = next
    ? [...MILESTONES].reverse().find((m) => m.at <= next.at && m.at !== next.at)?.at ?? 0
    : 0;
  const span = next ? next.at - prevAt : 1;
  const progressInSpan = next ? Math.max(0, totalDone - prevAt) : 1;
  const pct = next ? Math.min(100, Math.round((progressInSpan / span) * 100)) : 100;

  // Pulse the XP chip on each new win.
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (bumpKey === 0) return;
    setPulse(true);
    const t = window.setTimeout(() => setPulse(false), 900);
    return () => window.clearTimeout(t);
  }, [bumpKey]);

  return (
    <div className="mb-4 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div
          className={`inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/15 px-2.5 py-1 text-[11px] font-mono font-bold text-primary transition-transform ${
            pulse ? "scale-110" : "scale-100"
          }`}
        >
          <Sparkles className="w-3 h-3" />
          {totalXp.toLocaleString()} XP
        </div>
        <div
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[11px] font-mono font-bold text-primary"
          title="Live hackathon pot"
        >
          <Trophy className="w-3 h-3" />
          {potLabel} pot
        </div>
      </div>

      {next ? (
        <>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1 text-[11px] font-mono text-foreground">
              <Target className="w-3 h-3 text-primary" />
              {remaining} {remaining === 1 ? "win" : "wins"} to{" "}
              <span className="font-bold text-primary">{next.label}</span>
            </span>
            <span className="text-[10px] font-mono text-muted-foreground tabular-nums">
              {totalDone}/{next.at}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      ) : (
        <div className="text-[11px] font-mono text-primary text-center">
          🏆 Every milestone unlocked — you're a qualifier.
        </div>
      )}
    </div>
  );
};

export default WinTracker;